/**
 * Who may enter the staff CRM, and where the CRM lives.
 *
 * Three apps now need to answer both questions: the CRM itself gates its own
 * front door with them, and the admin center and the account hub draw a link
 * into the CRM only for somebody the CRM would actually let in. A rule copied
 * into three files drifts in three directions, and the failure mode is the
 * quiet one - a menu entry that leads to an "access denied" screen, or worse,
 * an entry a client can see on the portal.
 *
 * Pure and DOM-free on purpose: `crmUrl` takes the location rather than reading
 * `window`, so both halves are testable. See `crmAccess.test.ts`.
 */

/** The part of a signed-in user this module reads. */
export interface CrmAccessUser {
  isSuperadmin: boolean;
  memberships: { isHost: boolean }[];
}

/**
 * Whether this user may enter the staff CRM.
 *
 * The CRM is a PLATFORM tool: a superadmin, or a member of the host site. An
 * ordinary client of a tenant site never qualifies, which is also what keeps
 * the link off the client portal - the portal and the CRM share a domain, so
 * the audience check is the only thing separating them.
 */
export function hasCrmAccess(user: CrmAccessUser | null | undefined): boolean {
  if (!user) return false;
  return user.isSuperadmin || user.memberships.some((m) => m.isHost);
}

/** Where the CRM is served, for a browser standing on `hostname`. */
export interface CrmUrlLocation {
  hostname: string;
  protocol: string;
}

/** The CRM's own dev port (apps/web_crm/package.json). */
const CRM_DEV_URL = "http://localhost:3313";

/**
 * The CRM's public address.
 *
 * There is no `crm.husly.app`: nginx serves the CRM on `crm.webadmin.ee` only
 * (deployment/husly/nginx-husly.conf), so the label swap that works between the
 * admin center and the account hub would produce a dead link here. A host under
 * `webadmin.ee` gets the sibling on its own base domain, which keeps a future
 * white-label deployment working; everything else gets the platform address.
 *
 * From `husly.app` this is a cross-domain jump, so the session cookie
 * (`Domain=husly.app`) does not travel and the CRM asks for a sign-in. From
 * `portal.webadmin.ee` the cookie is `Domain=webadmin.ee` and the jump is
 * seamless. The link is worth having either way; it just is not always SSO.
 */
export const PLATFORM_CRM_URL = "https://crm.webadmin.ee";

export function crmUrl(location: CrmUrlLocation | null | undefined): string {
  if (!location) return CRM_DEV_URL;
  const hostname = location.hostname.split(":")[0]?.toLowerCase() ?? "";
  if (!hostname) return CRM_DEV_URL;
  if (hostname === "localhost" || hostname.endsWith(".localhost") || hostname === "127.0.0.1") {
    return CRM_DEV_URL;
  }
  const base = hostname.split(".").slice(-2).join(".");
  if (base === "webadmin.ee") return `${location.protocol}//crm.${base}`;
  return PLATFORM_CRM_URL;
}
